<!--
  管理端外壳组件（布局 + 导航 + 会话）。

  ⚠️ 全站**没有任何链接指向这里**：不在首页、导航、任务块页放按钮或文字，
     管理员靠手动输入 https://<域名>/admin 进入。要加入口需先确认产品口径。

  登录态独立于 C 端：token 存 localStorage['admin_token']，
  后端按 Sa-Token 的 loginType=admin 校验，与 C 端互不认领。

  视觉：走标准 B 端控制台布局（深色侧边导航 + 白色顶栏 + 中性灰内容区），
  样式集中在 @/styles/admin.css，本文件只管结构与交互。
-->
<template>
  <div class="admin-root">
    <AdminLogin v-if="!admin" @login-success="onLoginSuccess" />

    <div v-else class="adm-layout" :class="{ 'is-collapsed': collapsed }">
      <!-- ------------------------------ 侧边导航 ------------------------------ -->
      <aside class="adm-sider">
        <div class="adm-logo">
          <span class="adm-logo-mark">TB</span>
          <span class="adm-logo-text">
            <span class="adm-logo-title">TaskBlock</span>
            <span class="adm-logo-sub">运营管理后台</span>
          </span>
        </div>

        <nav class="adm-menu">
          <div v-for="group in menu" :key="group.title">
            <div class="adm-menu-group-title">{{ group.title }}</div>
            <div
              v-for="item in group.items"
              :key="item.key"
              class="adm-menu-item"
              :class="{ 'is-active': currentTab === item.key }"
              :title="item.label"
              @click="currentTab = item.key"
            >
              <i :class="item.icon" class="adm-menu-icon" />
              <span class="adm-menu-label">{{ item.label }}</span>
            </div>
          </div>
        </nav>

        <div class="adm-sider-footer">
          内部系统 · 全部操作留痕
        </div>
      </aside>

      <!-- ------------------------------ 右侧主体 ------------------------------ -->
      <div class="adm-main">
        <header class="adm-header">
          <div class="adm-header-left">
            <button
              class="adm-collapse-btn"
              :title="collapsed ? '展开菜单' : '收起菜单'"
              @click="collapsed = !collapsed"
            >
              <i :class="collapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'" />
            </button>
            <div class="adm-crumb">
              <span>管理端</span>
              <span class="adm-crumb-sep">/</span>
              <span class="adm-crumb-current">{{ currentLabel }}</span>
            </div>
          </div>

          <div class="adm-header-right">
            <div class="adm-user">
              <div class="adm-user-trigger" @click.stop="toggleUserMenu">
                <span class="adm-avatar">{{ avatarText }}</span>
                <span class="adm-user-name">{{ displayName }}</span>
                <span v-if="admin.role" class="adm-user-role">{{ roleText }}</span>
                <i class="el-icon-caret-bottom adm-user-caret" />
              </div>

              <div v-if="userMenuVisible" class="adm-dropdown">
                <div class="adm-dropdown-item" @click="openPasswordDialog">
                  <i class="el-icon-lock" />修改密码
                </div>
                <div class="adm-dropdown-divider" />
                <div class="adm-dropdown-item is-danger" @click="onLogout">
                  <i class="el-icon-switch-button" />退出登录
                </div>
              </div>
            </div>
          </div>
        </header>

        <main class="adm-content">
          <div class="adm-page-head">
            <div>
              <div class="adm-page-title">{{ currentLabel }}</div>
              <div class="adm-page-desc">{{ currentDesc }}</div>
            </div>
          </div>

          <OverviewPanel v-if="currentTab === 'overview'" />
          <UsersPanel
            v-else-if="currentTab === 'users'"
            ref="usersPanel"
            @browse-workspaces="onBrowseWorkspaces"
          />
          <WorkspacePanel
            v-else-if="currentTab === 'workspaces'"
            :preset="workspacePreset"
            @view-user="onViewUserFromWorkspace"
          />
          <AuditPanel v-else-if="currentTab === 'audit'" />
          <InviteCodePanel v-else-if="currentTab === 'invite-codes'" />
          <AchievementsPanel v-else-if="currentTab === 'achievements'" />
          <SettingsPanel v-else-if="currentTab === 'settings'" />
          <AppControlPanel v-else-if="currentTab === 'app-control'" />
          <LogsPanel v-else-if="currentTab === 'logs'" />
          <VerifyCodePanel v-else-if="currentTab === 'verify-codes'" />
          <AlertPanel v-else-if="currentTab === 'alerts'" />
        </main>
      </div>
    </div>

    <!-- ------------------------------ 修改密码 ------------------------------ -->
    <el-dialog
      title="修改密码"
      :visible.sync="pwdDialogVisible"
      width="440px"
      append-to-body
      @closed="onPwdDialogClosed"
    >
      <el-form label-width="88px" @submit.native.prevent="onChangePassword">
        <el-form-item label="当前密码">
          <el-input
            v-model="pwdForm.oldPassword"
            name="oldPassword"
            type="password"
            autocomplete="current-password"
            show-password
            placeholder="请输入当前密码"
          />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input
            v-model="pwdForm.newPassword"
            name="newPassword"
            type="password"
            autocomplete="new-password"
            show-password
            placeholder="8-64 位"
          />
        </el-form-item>
        <el-form-item label="确认新密码">
          <el-input
            v-model="pwdForm.confirmPassword"
            name="confirmPassword"
            type="password"
            autocomplete="new-password"
            show-password
            placeholder="再次输入新密码"
            @keyup.enter.native="onChangePassword"
          />
        </el-form-item>
      </el-form>
      <div class="adm-alert is-warning" style="margin-top: 4px;">
        <i class="el-icon-warning-outline" />
        <span>修改后立即生效，下次登录需使用新密码。</span>
      </div>
      <div slot="footer">
        <el-button size="small" @click="pwdDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="pwdLoading" @click="onChangePassword">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import '@/styles/admin.css'
import AdminLogin from './AdminLogin.vue'
import OverviewPanel from './OverviewPanel.vue'
import UsersPanel from './UsersPanel.vue'
import WorkspacePanel from './WorkspacePanel.vue'
import AuditPanel from './AuditPanel.vue'
import InviteCodePanel from './InviteCodePanel.vue'
import AchievementsPanel from './AchievementsPanel.vue'
import SettingsPanel from './SettingsPanel.vue'
import AppControlPanel from './AppControlPanel.vue'
import LogsPanel from './LogsPanel.vue'
import VerifyCodePanel from './VerifyCodePanel.vue'
import AlertPanel from './AlertPanel.vue'
import {
  adminMe,
  adminLogout,
  adminChangePassword,
  getAdminToken,
  clearAdminToken,
  isOk,
  isNotLogin
} from '@/api/admin'
import { adminTabTitle } from '@/utils/title'

/**
 * 侧边导航结构。
 * icon 一律用 Element UI 自带图标类（theme-chalk 全量 CSS 已引入，无需注册组件）。
 */
const MENU = [
  {
    title: '概览',
    items: [
      {
        key: 'overview',
        label: '概览',
        icon: 'el-icon-s-data',
        desc: '平台整体运行数据与最近操作记录'
      }
    ]
  },
  {
    title: '运营管理',
    items: [
      {
        key: 'users',
        label: '用户管理',
        icon: 'el-icon-user',
        desc: '查询 C 端账号，禁用 / 启用后立即生效并记录操作人'
      },
      {
        key: 'workspaces',
        label: '工作区浏览',
        icon: 'el-icon-folder-opened',
        desc: '按 工作区 → 工作表 → 时间块 → 评论 逐层查看 C 端内容，只读；评论可下架 / 恢复'
      },
      {
        key: 'audit',
        label: '内容审核',
        icon: 'el-icon-document',
        desc: '对随想、分享评论与分享（工作区 / 工作表，含好友授权）执行下架 / 恢复，操作需填写原因'
      },
      {
        key: 'invite-codes',
        label: '邀请码',
        icon: 'el-icon-tickets',
        desc: '批量生成邀请码，跟踪使用情况，支持禁用与作废'
      }
    ]
  },
  {
    title: '系统配置',
    items: [
      {
        key: 'achievements',
        label: '成就配置',
        icon: 'el-icon-medal',
        desc: '维护成就定义与解锁条件，支持上下架和解锁试算'
      },
      {
        key: 'settings',
        label: '系统设置',
        icon: 'el-icon-setting',
        desc: '调整全局开关与参数，保存后立即生效，无需重启服务'
      },
      {
        // APP 端云控：与「系统设置」刻意分开 —— 系统设置是后端进程读的配置，
        // 云控是下发给 App、由客户端生效的配置，两者消费方不同，混在一起会串味。
        key: 'app-control',
        label: 'APP 云控',
        icon: 'el-icon-mobile-phone',
        desc: '安卓端版本与更新、功能开关、安全策略（iOS / 鸿蒙预留）；按平台与环境隔离，客户端下次拉取即生效'
      },
      {
        key: 'logs',
        label: '操作日志',
        icon: 'el-icon-files',
        desc: '只读审计流水，记录管理端全部写操作与时间、IP'
      }
    ]
  },
  {
    // 排障专用：用户报「收不到验证码 / 验证码不对」时直接核对 Redis 里的真值；
    // 飞书告警机器人是「出问题时怎么第一时间知道」，两者都是运维视角，归在同一组。
    // 刻意各自独立成板块而不是塞进「系统设置」——它们不是纯配置项。
    title: '运维排障',
    items: [
      {
        key: 'verify-codes',
        label: '验证码',
        icon: 'el-icon-key',
        desc: '查看 Redis 中尚未过期的验证码（列表脱敏，看明文需输入完整手机号/邮箱），用于排查「收不到码 / 验证码不对」'
      },
      {
        key: 'alerts',
        label: '飞书告警',
        icon: 'el-icon-bell',
        desc: '异常推送到飞书群：查看链路状态、发送测试消息、翻最近的发送记录'
      }
    ]
  }
]

const ROLE_TEXT = {
  SUPER_ADMIN: '超级管理员',
  ADMIN: '管理员',
  OPERATOR: '运营'
}

export default {
  name: 'AdminPage',
  components: {
    AdminLogin,
    OverviewPanel,
    UsersPanel,
    WorkspacePanel,
    AuditPanel,
    InviteCodePanel,
    AchievementsPanel,
    SettingsPanel,
    AppControlPanel,
    LogsPanel,
    VerifyCodePanel,
    AlertPanel
  },
  data() {
    return {
      admin: null,
      currentTab: 'overview',
      // 用户详情 → 工作区浏览 的初始过滤：{ userId, ts }。
      // ts 保证同一用户两次进入也是新对象，WorkspacePanel 的 watch 必触发。
      workspacePreset: null,
      menu: MENU,
      collapsed: false,
      userMenuVisible: false,
      pwdDialogVisible: false,
      pwdLoading: false,
      pwdForm: { oldPassword: '', newPassword: '', confirmPassword: '' }
    }
  },
  computed: {
    currentMeta() {
      for (let i = 0; i < MENU.length; i += 1) {
        const hit = MENU[i].items.find(item => item.key === this.currentTab)
        if (hit) return hit
      }
      return { label: '管理端', desc: '' }
    },
    currentLabel() {
      return this.currentMeta.label
    },
    currentDesc() {
      return this.currentMeta.desc
    },
    displayName() {
      return (this.admin && (this.admin.nickname || this.admin.username)) || '管理员'
    },
    avatarText() {
      return String(this.displayName).trim().charAt(0).toUpperCase()
    },
    roleText() {
      return ROLE_TEXT[this.admin && this.admin.role] || (this.admin && this.admin.role) || ''
    }
  },
  watch: {
    // 管理端内部标签（非独立路由）切换时同步专属标题
    currentTab: {
      immediate: true,
      handler() {
        adminTabTitle(this.currentLabel)
      }
    }
  },
  created() {
    if (getAdminToken()) this.loadMe()
  },
  mounted() {
    // 弹窗 / Message 挂在 body 下，靠这个 class 启用管理端皮肤（见 admin.css）
    document.body.classList.add('admin-mode')
    document.addEventListener('click', this.closeUserMenu)
  },
  beforeDestroy() {
    document.body.classList.remove('admin-mode')
    document.removeEventListener('click', this.closeUserMenu)
  },
  methods: {
    loadMe() {
      adminMe()
        .then(res => {
          // 会话过期（9501）不弹错，直接回登录页
          if (isNotLogin(res)) {
            clearAdminToken()
            this.admin = null
            return
          }
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '获取管理员信息失败')
          }
          this.admin = res.data
        })
        .catch(err => {
          this.$message.error(err.message || '获取管理员信息失败')
          clearAdminToken()
          this.admin = null
        })
    },
    onLoginSuccess(data) {
      this.admin = {
        id: data.adminId,
        nickname: data.nickname,
        role: data.role
      }
      // 登录响应只有 id/nickname/role，完整信息再拉一次
      this.loadMe()
    },
    toggleUserMenu() {
      this.userMenuVisible = !this.userMenuVisible
    },
    closeUserMenu() {
      this.userMenuVisible = false
    },
    openPasswordDialog() {
      this.userMenuVisible = false
      this.pwdDialogVisible = true
    },
    onPwdDialogClosed() {
      this.pwdForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
    },
    onLogout() {
      this.userMenuVisible = false
      adminLogout()
        .catch(() => {})
        .finally(() => {
          clearAdminToken()
          this.admin = null
          this.currentTab = 'overview'
        })
    },
    /**
     * 用户详情 → 工作区浏览：带上 userId 初始过滤跳过去。
     * 排障路径「这个用户说他数据不对」→ 直接落在他名下的工作区列表。
     */
    onBrowseWorkspaces(userId) {
      this.workspacePreset = { userId: Number(userId), ts: Date.now() }
      this.currentTab = 'workspaces'
    },
    /**
     * 工作区浏览 → 用户详情：反向跳回用户页并直接弹详情。
     *
     * ⚠️ UsersPanel 是 v-else-if 渲染的，切走即销毁 —— 必须等 nextTick
     * 组件挂载后再通过 $refs 调 openDetailById，否则 ref 还是上一次的（或不存在）。
     */
    onViewUserFromWorkspace(userId) {
      this.currentTab = 'users'
      this.$nextTick(() => {
        const panel = this.$refs.usersPanel
        if (panel) panel.openDetailById(userId)
      })
    },
    onChangePassword() {
      if (this.pwdLoading) return
      if (!this.pwdForm.oldPassword || !this.pwdForm.newPassword) {
        this.$message.warning('请输入当前密码与新密码')
        return
      }
      if (this.pwdForm.newPassword.length < 8) {
        this.$message.warning('新密码至少 8 位')
        return
      }
      if (this.pwdForm.newPassword !== this.pwdForm.confirmPassword) {
        this.$message.warning('两次输入的新密码不一致')
        return
      }
      this.pwdLoading = true
      adminChangePassword(this.pwdForm.oldPassword, this.pwdForm.newPassword)
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '修改失败')
          }
          this.$message.success('密码已修改')
          this.pwdDialogVisible = false
          this.pwdForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
        })
        .catch(err => {
          this.$message.error(err.message || '修改失败')
        })
        .finally(() => {
          this.pwdLoading = false
        })
    }
  }
}
</script>

<style scoped>
/* 布局与配色全部来自 @/styles/admin.css，这里只放少量本组件私有规则 */
.adm-page-head {
  margin-bottom: 16px;
}

.adm-dropdown-item i {
  font-size: 14px;
}
</style>
